import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { ApiService } from 'src/app/core/services/api.service';
import { PdfService } from 'src/app/core/services/pdf.service';
import { AviPdfRendererService } from 'src/app/core/services/avi-pdf-renderer.service';
import { AviEvent, AviProjection, AviRecord, AviReportResponse, AviRisk, normalizeAviReport } from 'src/app/core/models/avi-report.model';

@Component({
  selector: 'app-avi-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './avi-report.component.html',
  styleUrls: ['./avi-report.component.scss']
})
export class AviReportComponent implements OnInit, OnChanges {
  @Input({ required: true }) uuid = '';
  @Input() report: any;

  private readonly api = inject(ApiService);
  private readonly pdf = inject(PdfService);
  private readonly pdfRenderer = inject(AviPdfRendererService);
  response: AviReportResponse | null = null;
  avi: AviProjection | null = null;
  loading = false;
  processing = false;
  notFound = false;
  error = '';
  pdfLoading = false;

  readonly ownershipFields = [
    ['name', 'Name'], ['relationshipType', 'Relationship'], ['ownershipPercentage', 'Direct ownership'],
    ['country', 'Country'], ['asOfDate', 'As-of date'], ['actingFor', 'Acting for'], ['reasoning', 'Reasoning']
  ];
  readonly companyFields = [
    ['companyName', 'Company name'], ['country', 'Country'], ['address', 'Address'],
    ['city', 'City'], ['legalForm', 'Legal form'], ['registrationNumber', 'Registration number'],
    ['localCode', 'Local registration code'], ['leiCode', 'LEI'], ['website', 'Website']
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uuid']?.currentValue) this.load();
  }

  ngOnInit(): void {
    if (this.uuid && !this.response && !this.loading) this.load();
  }

  load(): void {
    this.loading = true;
    this.processing = false;
    this.notFound = false;
    this.error = '';
    this.api.getAviReport(this.uuid).subscribe({
      next: value => {
        this.response = normalizeAviReport(value);
        this.avi = this.response.avi;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.processing = error?.status === 409;
        this.notFound = error?.status === 404;
        this.error = !this.processing && !this.notFound ? 'Unable to load the AVI report. Please try again.' : '';
      }
    });
  }

  downloadPdf(): void {
    if (!this.response || this.pdfLoading) return;
    this.pdfLoading = true;
    this.pdf.generateAviReportPdf(this.response, this.report, this.pdfRenderer)
      .finally(() => this.pdfLoading = false);
  }

  display(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return 'N/A';
    return String(value);
  }

  list(value: unknown): any[] { return Array.isArray(value) ? value : []; }

  field(record: AviRecord | null | undefined, key: string): unknown {
    return record ? record[key] : null;
  }

  partyField(record: AviRecord | null | undefined, key: string): unknown {
    if (!record) return null;
    return record[key]
      ?? (record['vesselData'] as AviRecord | undefined)?.[key]
      ?? (record['tevunaData'] as AviRecord | undefined)?.[key]
      ?? null;
  }

  isUrl(value: unknown): boolean { return typeof value === 'string' && /^https?:\/\//i.test(value); }

  vesselTitle(): string {
    const imo = this.display(this.avi?.imo);
    const name = this.avi?.vesselName?.trim();
    return name && name !== imo && !/^\d{7}$/.test(name) ? name : `Vessel IMO ${imo}`;
  }

  reportTitle(): string {
    return this.response?.report.title || this.vesselTitle();
  }

  eventCoordinates(event: any): string {
    const coordinates = event?.geometry?.coordinates;
    return Array.isArray(coordinates) && coordinates.length >= 2
      ? `Longitude ${this.display(coordinates[0])} · Latitude ${this.display(coordinates[1])}`
      : 'N/A';
  }

  riskEvents(risk: any): any[] { return this.list(risk?.events); }

  chronology(): Array<{ date: string; label: string; detail: string }> {
    if (!this.avi) return [];
    const entries: Array<{ date: string; label: string; detail: string }> = [];
    const add = (date: unknown, label: string, detail: unknown) => {
      if (date === null || date === undefined || date === '') return;
      entries.push({ date: this.display(date), label, detail: this.display(detail) });
    };
    this.list(this.avi.ownership).forEach((record: AviRecord) => {
      add(record['asOfDate'] ?? record['startDate'], 'Ownership record', record['name']);
    });
    this.list(this.avi.ubos).forEach((record: AviRecord) => {
      add(record['uboStartDate'] ?? record['startDate'], 'UBO record', record['name']);
    });
    [this.avi.vesselRisk, ...this.avi.entityRisk].forEach((risk: AviRisk) => {
      this.riskEvents(risk).forEach((event: AviEvent) => {
        add(event.event_date ?? event['date'], 'Risk / incident', event.description);
      });
    });
    return entries;
  }

  trackByIndex(index: number): number { return index; }
}
